import { AlertTriangle, Gamepad2, MonitorCog } from "lucide-react";
import { fmtDate } from "../lib/labels";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const ACTIONS = {
  declarar_guerra: "Declarar guerra", cancelar_guerra: "Cancelar guerra", listar_zonas: "Listar zonas",
  abrir_mesa: "Abrir mesa de craft", iniciar_craft: "Iniciar craft", concluir_craft: "Concluir craft",
  abortar_craft: "Craft abortado", reportar_kill: "Reportar kill", spam_eventos: "Spam de eventos",
  login: "Login no painel", org_criar: "Criar organização", org_editar: "Editar organização",
  org_eliminar: "Eliminar organização", org_reset_cooldown: "Repor cooldown", zona_criar: "Criar zona",
  zona_editar: "Editar zona", zona_eliminar: "Eliminar zona", zona_avancar_pontos: "Avançar pontos de posse",
  receita_criar: "Criar receita", receita_editar: "Editar receita", receita_eliminar: "Eliminar receita",
  definicoes_editar: "Editar configurações", guerra_forcar_cancelar: "Forçar cancelamento",
  guerra_avancar: "Avançar guerra", temporada_fechar: "Fechar temporada", configuracao_importar: "Importar configuração",
  backup_create: "Criar cópia de segurança", backup_restore: "Restaurar cópia de segurança", backup_download: "Descarregar cópia de segurança",
};

export const actionLabel = (a) => ACTIONS[a] || a;

export const actorLabel = (a = {}) => a.name || a.email || a.citizenid || "—";

export const AuditSelect = ({ value, onChange, placeholder, options, testid, className = "w-44" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${className} border-slate-700 bg-slate-900`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-option-${o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const SourceTag = ({ source }) => {
  const Icon = source === "game" ? Gamepad2 : MonitorCog;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400">
      <Icon className="h-3.5 w-3.5" />{source === "game" ? "Jogo" : "Painel"}
    </span>
  );
};

export const ResultTag = ({ result, testid }) => (
  <span data-testid={testid} className={`border px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest ${
    result === "aceite" ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`}>
    {result === "aceite" ? "Aceite" : "Recusado"}
  </span>
);

export const AuditRow = ({ e, onOpen }) => (
  <tr onClick={() => onOpen(e)} data-testid={`audit-row-${e.id}`}
    className={`cursor-pointer border-b transition-colors ${e.suspicious
      ? "border-rose-900/60 bg-rose-950/40 text-rose-100 hover:bg-rose-900/40" : "border-slate-800/70 hover:bg-slate-900/60"}`}>
    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400">{fmtDate(e.timestamp)}</td>
    <td className="px-4 py-3"><SourceTag source={e.source} /></td>
    <td className="px-4 py-3 text-sm">
      <div className="font-medium">{actorLabel(e.actor)}</div>
      {e.actor?.gang && <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{e.actor.gang}</div>}
    </td>
    <td className="px-4 py-3 text-sm">
      <span className="inline-flex items-center gap-1.5">
        {e.suspicious && <AlertTriangle className="h-3.5 w-3.5 text-rose-400" data-testid={`audit-suspicious-icon-${e.id}`} />}
        {actionLabel(e.action)}
      </span>
    </td>
    <td className="px-4 py-3 text-sm text-slate-300">{e.zone?.name || e.target || "—"}</td>
    <td className="px-4 py-3"><ResultTag result={e.result} testid={`audit-result-${e.id}`} /></td>
    <td className="max-w-xs truncate px-4 py-3 text-xs text-slate-400" title={e.reason || ""}>{e.reason || "—"}</td>
  </tr>
);

const KV = ({ k, v }) => (
  <div className="grid grid-cols-[130px_1fr] gap-3 border-b border-slate-800/70 py-1.5 text-sm">
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{k}</span>
    <span className="break-all text-slate-200">{v ?? "—"}</span>
  </div>
);

const show = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

const DiffTable = ({ changes }) => (
  <table className="w-full text-left text-xs" data-testid="audit-detail-diff">
    <thead className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
      <tr><th className="py-1.5 pr-3">Campo</th><th className="py-1.5 pr-3">Antes</th><th className="py-1.5">Depois</th></tr>
    </thead>
    <tbody>
      {Object.entries(changes).map(([k, c]) => (
        <tr key={k} className="border-t border-slate-800/70 align-top">
          <td className="py-1.5 pr-3 font-mono text-slate-400">{k}</td>
          <td className="break-all py-1.5 pr-3 text-rose-300">{show(c.antes)}</td>
          <td className="break-all py-1.5 text-emerald-300">{show(c.depois)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Details = ({ details }) => {
  const { alteracoes, ...rest } = details || {};
  const hasChanges = alteracoes && Object.keys(alteracoes).length > 0;
  const hasRest = Object.keys(rest).length > 0;
  if (!hasChanges && !hasRest && !alteracoes) return null;
  return (
    <div className="mt-4 space-y-3">
      <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Detalhes</div>
      {alteracoes && (hasChanges ? <DiffTable changes={alteracoes} /> : <div className="text-xs text-slate-500">Sem alterações.</div>)}
      {hasRest && <pre data-testid="audit-detail-json" className="max-h-64 overflow-auto border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] text-slate-300">{JSON.stringify(rest, null, 2)}</pre>}
    </div>
  );
};

export const AuditDetail = ({ entry, onClose }) => {
  const a = entry?.actor || {};
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-950" data-testid="audit-detail-dialog">
        {entry && <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-wider">
              {entry.suspicious && <AlertTriangle className="h-4 w-4 text-rose-400" />}{actionLabel(entry.action)}
            </DialogTitle>
            <DialogDescription className="text-slate-500">Detalhe do registo de auditoria.</DialogDescription>
          </DialogHeader>
          {entry.suspicious && <div data-testid="audit-detail-suspicious" className="border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">Evento marcado como suspeito.</div>}
          <div>
            <KV k="Data" v={fmtDate(entry.timestamp)} />
            <KV k="Origem" v={entry.source === "game" ? "Jogo" : "Painel"} />
            <KV k="Resultado" v={<ResultTag result={entry.result} />} />
            {entry.reason && <KV k="Motivo" v={entry.reason} />}
            <KV k="Ator" v={actorLabel(a)} />
            {a.citizenid && <KV k="CitizenID" v={a.citizenid} />}
            {a.license && <KV k="Licença" v={a.license} />}
            {a.server_id != null && <KV k="ID no servidor" v={a.server_id} />}
            {a.gang && <KV k="Organização" v={`${a.gang}${a.org_source ? ` (via ${a.org_source})` : ""}${a.gang_grade != null ? ` · grau ${a.gang_grade}` : ""}${a.grade_name ? ` "${a.grade_name}"` : ""}`} />}
            {(a.qb_gang || a.qb_job) && <KV k="QBCore" v={`gang: ${a.qb_gang || "none"}, job: ${a.qb_job || "none"}`} />}
            {entry.zone && <KV k="Zona" v={entry.zone.name || entry.zone.id} />}
            {entry.target && <KV k="Alvo" v={entry.target} />}
            {entry.ip && <KV k="IP" v={entry.ip} />}
          </div>
          <Details details={entry.details} />
        </>}
      </DialogContent>
    </Dialog>
  );
};
